import type { User } from '../api/types';

function isSafeRelativePath(p?: string | null): p is string {
  if (!p) return false;
  if (p.startsWith('http://') || p.startsWith('https://')) return false;
  return p.startsWith('/');
}

function coerceStep(step: unknown): number {
  if (typeof step === 'number' && Number.isFinite(step) && step >= 0) return Math.floor(step);
  if (typeof step === 'string') {
    const n = Number(step);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  return 0;
}

/**
 * Check if there is an in-progress UW journey persisted in localStorage.
 * Returns the resume URL if found, null otherwise.
 */
function getPersistedUWJourneyRedirect(): string | null {
  try {
    const raw = localStorage.getItem('uw-journey-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state;
    if (!state?.applicationId) return null;
    // Only redirect if the journey hasn't been submitted yet
    if (state.submissionStatus === 'submitted') return null;
    const step = coerceStep(state.currentStep);
    return `/uw/onboarding/${state.applicationId}?step=${step}`;
  } catch {
    return null;
  }
}

export function getOnboardingRedirect(
  user?: User | null,
  opts?: { from?: string | null; defaultComplete?: string }
): string | null {
  const defaultComplete = opts?.defaultComplete || '/dashboard';

  const onboarding = (user as any)?.onboarding as
    | { completed: boolean; currentStep?: number | string }
    | undefined;

  // If onboarding missing or not completed → go to new-onboarding step
  if (!onboarding || onboarding.completed === false) {
    const step = coerceStep(onboarding?.currentStep);
    const stepClamped = Math.max(0, step); // UI clamps upper bound itself
    return `/new-onboarding?step=${stepClamped}`;
  }

  // Check for in-progress UW journey (persisted in localStorage)
  const uwResume = getPersistedUWJourneyRedirect();
  if (uwResume) return uwResume;

  // Onboarding complete → prefer a safe "from" route if present
  if (isSafeRelativePath(opts?.from)) {
    return opts!.from!;
  }
  return defaultComplete;
}